import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { NOTIFICATION_TYPE, useNotificationCenter } from 'context/NotificationCenterContext';
import type { AppNotification } from 'context/NotificationCenterContext';
import {
  getEventCategories,
  useNotificationFirmCompanies,
  useNotificationFirms,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from 'services/hooks/useNotificationPreferences';
import { useUserUtils } from 'utilities/userUtils/useUserUtils';
import NotificationCard from './NotificationCard';
import {
  notificationsListSx,
  panelHeaderSx,
  panelPaperSx,
  prefSectionSx,
  prefSubheaderSx,
} from './NotificationCenter.styles';
import { compareNotificationPanelOrder } from './NotificationCenter.utils';

interface NotificationPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  notifications: AppNotification[];
  onClose: () => void;
  onDismiss: (id: string) => void;
  showGlobalToggle?: boolean;
}

const SWITCH_SX = {
  width: 36,
  height: 20,
  padding: '2px',
  marginRight: '-2px',
  flexShrink: 0,
  '& .MuiSwitch-thumb': { width: 14, height: 14, boxShadow: 'none', color: '#fff' },
  '& .MuiSwitch-switchBase': {
    padding: '3px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#3B5BF6' },
    },
  },
  '& .MuiSwitch-track': { borderRadius: 10, opacity: 1, backgroundColor: '#d0d0d0' },
};

const NotificationPanel = ({ open, anchorEl, notifications, onClose, onDismiss, showGlobalToggle = false }: NotificationPanelProps) => {
  const { markNotificationRead, dismissAll, markAllRead, browserNotifications, globalNotifEnabled, toggleGlobalNotifications } = useNotificationCenter();
  const { enabled: browserEnabled, permission: browserPermission, requestAndEnable, disable: disableBrowser } = browserNotifications;

  const [showPreferences, setShowPreferences] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const [companiesSearch, setCompaniesSearch] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());

  const visible = useMemo(
    () => notifications
      .filter(n => !n.dismissed && !(
        showGlobalToggle && !globalNotifEnabled &&
        (n.type === NOTIFICATION_TYPE.GLOBAL || n.type === NOTIFICATION_TYPE.GENERAL)
      ))
      .sort(compareNotificationPanelOrder),
    [notifications, showGlobalToggle, globalNotifEnabled]
  );
  const [selectedFirmId, setSelectedFirmId] = useState<number | null>(null);
  const companiesTriggerRef = useRef<HTMLDivElement | null>(null);
  const skipCompaniesCloseRef = useRef(false);
  const firmSelectOpenRef = useRef(false);

  const { data: preferences, isLoading } = useNotificationPreferences();
  const { mutate: updatePreferences } = useUpdateNotificationPreferences();
  const { data: firms = [], isLoading: isLoadingFirms } = useNotificationFirms(showPreferences);
  const { data: firmCompanies = [], isLoading: isLoadingFirmCompanies } = useNotificationFirmCompanies(
    selectedFirmId,
    showPreferences && selectedFirmId !== null
  );

  const [{ companyList }] = useStore();
  const { isStaffUser } = useUserUtils();
  const eventCategories = useMemo(() => getEventCategories(isStaffUser()), [isStaffUser]);
  const currentFirmCompanies = useMemo(() => (companyList ?? []) as FirmCompany[], [companyList]);

  // Detect current firm slug from URL and match against the firms list.
  const currentFirmSlug = useMemo(() => {
    const match = window.location.pathname.match(/\/firms\/([^/]+)/);
    return match?.[1] ?? null;
  }, []);

  // Once firms load, default selectedFirmId to the current firm (by slug) or the first firm.
  useEffect(() => {
    if (!firms.length || selectedFirmId !== null) {
      return;
    }
    const match = currentFirmSlug ? firms.find(f => f.slug === currentFirmSlug) : null;
    setSelectedFirmId(match?.id ?? firms[0].id);
  }, [firms, currentFirmSlug, selectedFirmId]);

  const excludedSet = useMemo(() => new Set(preferences?.excluded_event_types ?? []), [preferences]);

  const excludedCompanyIds = useMemo(() => preferences?.excluded_companies ?? [], [preferences]);

  const allCompanies = preferences?.all_companies ?? true;

  // Companies of the currently selected firm that will receive notifications (not excluded).
  const notExcludedInFirm = useMemo(
    () => firmCompanies.filter(c => !excludedCompanyIds.includes(c.company_id)),
    [firmCompanies, excludedCompanyIds]
  );

  const allFirmSelected =
    firmCompanies.length > 0 && !firmCompanies.some(c => excludedCompanyIds.includes(c.company_id));

  const someFirmSelected = firmCompanies.some(c => !excludedCompanyIds.includes(c.company_id));

  const filteredFirmCompanies = useMemo(() => {
    const q = companiesSearch.trim().toLowerCase();
    return q ? firmCompanies.filter(c => c.company_name.toLowerCase().includes(q)) : firmCompanies;
  }, [firmCompanies, companiesSearch]);

  // ── Event type helpers ──────────────────────────────────────────────────────

  const getCategoryState = useCallback(
    (events: readonly string[]): 'checked' | 'indeterminate' | 'unchecked' => {
      // "checked" means the user receives these events (i.e. NOT excluded).
      const enabledCount = events.filter(e => !excludedSet.has(e)).length;
      if (enabledCount === 0) {
        return 'unchecked';
      }
      if (enabledCount === events.length) {
        return 'checked';
      }
      return 'indeterminate';
    },
    [excludedSet]
  );

  const handleCategoryToggle = useCallback(
    (events: readonly string[], currentState: 'checked' | 'indeterminate' | 'unchecked') => {
      const next = new Set(excludedSet);
      if (currentState === 'checked') {
        // Currently receiving all → exclude all in this category.
        events.forEach(e => next.add(e));
      } else {
        // Currently partial/none → un-exclude everything in this category.
        events.forEach(e => next.delete(e));
      }
      updatePreferences({ excluded_event_types: Array.from(next) });
    },
    [excludedSet, updatePreferences]
  );

  // ── Company helpers ─────────────────────────────────────────────────────────

  const handleAllCompaniesToggle = useCallback(() => {
    updatePreferences({ all_companies: !allCompanies });
  }, [allCompanies, updatePreferences]);

  const handleSelectAllFirmCompanies = useCallback(() => {
    if (allFirmSelected) {
      // Deselect all → add all firm companies to exclusions
      const merged = new Set([...excludedCompanyIds, ...firmCompanies.map(c => c.company_id)]);
      updatePreferences({ excluded_companies: Array.from(merged) });
    } else {
      // Select all → remove all firm companies from exclusions
      const firmIds = new Set(firmCompanies.map(c => c.company_id));
      updatePreferences({ excluded_companies: excludedCompanyIds.filter(id => !firmIds.has(id)) });
    }
  }, [allFirmSelected, firmCompanies, excludedCompanyIds, updatePreferences]);

  const handleCompanyToggle = useCallback(
    (companyId: number) => {
      const next = new Set(excludedCompanyIds);
      if (next.has(companyId)) {
        next.delete(companyId); // Un-exclude → will receive
      } else {
        next.add(companyId); // Exclude → won't receive
      }
      updatePreferences({ excluded_companies: Array.from(next) });
    },
    [excludedCompanyIds, updatePreferences]
  );

  const companiesLabel = (() => {
    if (allCompanies) {
      return 'All companies (all firms)';
    }
    if (notExcludedInFirm.length === 0) {
      return 'None selected';
    }
    if (notExcludedInFirm.length === 1) {
      return notExcludedInFirm[0].company_name;
    }
    return `${notExcludedInFirm.length} companies selected`;
  })();

  const selectedFirmName = firms.find(f => f.id === selectedFirmId)?.name ?? '';

  const handleCloseCompanies = useCallback(() => {
    if (skipCompaniesCloseRef.current) {
      skipCompaniesCloseRef.current = false;
      return;
    }
    setCompaniesOpen(false);
    setCompaniesSearch('');
  }, []);

  const handleClickAway = useCallback(() => {
    if (firmSelectOpenRef.current) {
      return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setCompaniesOpen(false);
      setCompaniesSearch('');
      return undefined;
    }
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 10000);
    return () => window.clearInterval(intervalId);
  }, [open]);

  const hasAnyCompanies = currentFirmCompanies.length > 0 || firms.length > 0;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      sx={{ zIndex: 1400 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper data-testid="notification-panel" sx={panelPaperSx} elevation={8}>
          {/* Main header */}
          <Box sx={{ ...panelHeaderSx, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2, color: '#111' }}>
                Notifications
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
                Temporary updates while you&apos;re active
              </Typography>
            </Box>
            {visible.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mt: 0.25 }}>
                <Typography
                  component="button"
                  data-testid="notification-mark-all-read"
                  onClick={markAllRead}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    lineHeight: 1,
                    '&:hover': { textDecoration: 'underline' },
                  }}>
                  Mark all as read
                </Typography>
                <Typography
                  component="button"
                  data-testid="notification-clear-all"
                  onClick={dismissAll}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    lineHeight: 1,
                    '&:hover': { textDecoration: 'underline', color: '#111' },
                  }}>
                  Clear all
                </Typography>
              </Box>
            )}
          </Box>

          {/* Desktop notifications — always visible */}
          <Box sx={{ px: 2.5, py: 1.25, borderBottom: showGlobalToggle ? 'none' : '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#111' }}>Desktop notifications</Typography>
              <Switch
                size="small"
                checked={browserEnabled}
                onChange={() => (browserEnabled ? disableBrowser() : requestAndEnable())}
                inputProps={{ 'data-testid': 'notification-desktop-toggle', 'aria-label': 'Desktop notifications' } as React.InputHTMLAttributes<HTMLInputElement>}
                sx={SWITCH_SX}
              />
            </Box>
            {browserPermission === 'denied' && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.4 }}>
                Blocked. Reset permissions in your browser to enable.
              </Typography>
            )}
          </Box>

          {showGlobalToggle && (
            <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.9rem', color: '#111' }}>Global notifications</Typography>
                <Switch
                  size="small"
                  checked={globalNotifEnabled}
                  onChange={toggleGlobalNotifications}
                  inputProps={{ 'data-testid': 'notification-global-toggle', 'aria-label': 'Global notifications' } as React.InputHTMLAttributes<HTMLInputElement>}
                  sx={SWITCH_SX}
                />
              </Box>
            </Box>
          )}

          {/* Preferences bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.25,
              borderBottom: showPreferences ? 'none' : '1px solid #f0f0f0',
            }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111' }}>
              Receive notifications for
            </Typography>
            <IconButton
              data-testid="notification-preferences-toggle"
              size="small"
              onClick={() => setShowPreferences(v => !v)}
              sx={{
                border: '1px solid',
                borderColor: showPreferences ? 'primary.main' : '#e0e0e0',
                borderRadius: '6px',
                p: 0.5,
                color: showPreferences ? 'primary.main' : '#888',
                '&:hover': { backgroundColor: 'action.hover' },
              }}>
              <SettingsIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>

          {/* Preferences body */}
          {showPreferences && (
            <Box data-testid="notification-preferences-panel" sx={{ ...prefSectionSx, pt: 0, borderBottom: '1px solid #f0f0f0' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <>
                  {/* Event Types */}
                  {eventCategories.map(({ label, events }) => {
                    const state = getCategoryState(events);
                    return (
                      <Box
                        key={label}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
                        <Typography sx={{ fontSize: '0.9rem', color: '#111' }}>{label}</Typography>
                        <Switch
                          size="small"
                          checked={state !== 'unchecked'}
                          onChange={() => handleCategoryToggle(events, state)}
                          sx={SWITCH_SX}
                        />
                      </Box>
                    );
                  })}

                  {/* Companies */}
                  {hasAnyCompanies && (
                    <>
                      <Typography sx={{ ...prefSubheaderSx, mt: 1.5 }}>Companies</Typography>

                      {/* All companies (all firms) checkbox */}
                      <Box
                        onClick={handleAllCompaniesToggle}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mt: 0.75,
                          mb: 1,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}>
                        <Checkbox
                          size="small"
                          checked={allCompanies}
                          onChange={handleAllCompaniesToggle}
                          onClick={e => e.stopPropagation()}
                          sx={{ p: 0.5, mr: 0.5 }}
                        />
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>
                          All companies (all firms)
                        </Typography>
                      </Box>

                      <Divider sx={{ mb: 1 }} />

                      {/* Firm selector */}
                      {isLoadingFirms ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={16} />
                        </Box>
                      ) : (
                        <FormControl size="small" fullWidth disabled={allCompanies} sx={{ mb: 1 }}>
                          <InputLabel sx={{ fontSize: '0.82rem' }}>Firm</InputLabel>
                          <Select
                            value={selectedFirmId ?? ''}
                            label="Firm"
                            onOpen={() => {
                              firmSelectOpenRef.current = true;
                            }}
                            onClose={() => {
                              firmSelectOpenRef.current = false;
                            }}
                            onChange={e => {
                              setSelectedFirmId(Number(e.target.value));
                              setCompaniesOpen(false);
                              setCompaniesSearch('');
                            }}
                            MenuProps={{ sx: { zIndex: 1600 } }}
                            sx={{ fontSize: '0.82rem' }}>
                            {firms.map(f => (
                              <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.82rem' }}>
                                {f.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {/* Company list trigger */}
                      <Box
                        ref={companiesTriggerRef}
                        onMouseDown={() => {
                          if (companiesOpen) {
                            skipCompaniesCloseRef.current = true;
                          }
                        }}
                        onClick={() => {
                          if (!allCompanies) {
                            setCompaniesOpen(v => !v);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1.5,
                          py: 0.75,
                          border: '1px solid',
                          borderColor: companiesOpen ? 'primary.main' : '#c4c4c4',
                          borderRadius: '4px',
                          cursor: allCompanies ? 'default' : 'pointer',
                          userSelect: 'none',
                          opacity: allCompanies ? 0.45 : 1,
                          '&:hover': { borderColor: allCompanies ? '#c4c4c4' : '#111' },
                        }}>
                        <Typography
                          sx={{
                            fontSize: '0.82rem',
                            color: notExcludedInFirm.length === 0 && !allCompanies ? '#999' : '#111',
                          }}>
                          {companiesLabel}
                        </Typography>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: '1.1rem',
                            color: '#666',
                            transform: companiesOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>

                      {/* Company dropdown */}
                      <Popper
                        open={companiesOpen && !allCompanies}
                        anchorEl={companiesTriggerRef.current}
                        placement="bottom-start"
                        sx={{ zIndex: 1600, width: companiesTriggerRef.current?.offsetWidth ?? 'auto' }}
                        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}>
                        <ClickAwayListener onClickAway={handleCloseCompanies}>
                          <Paper
                            elevation={4}
                            sx={{ border: '1px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
                            {/* Search */}
                            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                              <OutlinedInput
                                size="small"
                                autoFocus
                                placeholder="Search companies..."
                                value={companiesSearch}
                                onChange={e => setCompaniesSearch(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                startAdornment={
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '1rem', color: '#aaa' }} />
                                  </InputAdornment>
                                }
                                sx={{ width: '100%', fontSize: '0.82rem', '& .MuiOutlinedInput-input': { py: 0.6 } }}
                              />
                            </Box>

                            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                              {isLoadingFirmCompanies ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                  <CircularProgress size={18} />
                                </Box>
                              ) : (
                                <>
                                  {/* Select all for this firm — only when not searching */}
                                  {!companiesSearch && firmCompanies.length > 0 && (
                                    <>
                                      <MenuItem
                                        dense
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={handleSelectAllFirmCompanies}>
                                        <Checkbox
                                          size="small"
                                          checked={allFirmSelected}
                                          indeterminate={someFirmSelected && !allFirmSelected}
                                          sx={{ p: 0.5 }}
                                        />
                                        <ListItemText
                                          primary={`Select all${selectedFirmName ? ` (${selectedFirmName})` : ''}`}
                                          primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }}
                                        />
                                      </MenuItem>
                                      <Divider />
                                    </>
                                  )}

                                  {/* Company list */}
                                  {filteredFirmCompanies.map(c => (
                                    <MenuItem
                                      key={c.company_id}
                                      dense
                                      onClick={() => handleCompanyToggle(c.company_id)}>
                                      <Checkbox
                                        size="small"
                                        checked={!excludedCompanyIds.includes(c.company_id)}
                                        sx={{ p: 0.5 }}
                                      />
                                      <ListItemText
                                        primary={c.company_name}
                                        primaryTypographyProps={{ fontSize: '0.82rem' }}
                                      />
                                    </MenuItem>
                                  ))}

                                  {filteredFirmCompanies.length === 0 && (
                                    <Box sx={{ px: 2, py: 1.5 }}>
                                      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                                        No results
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                          </Paper>
                        </ClickAwayListener>
                      </Popper>

                      {notExcludedInFirm.length === 0 && !allCompanies && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.75, lineHeight: 1.4 }}>
                          No companies selected — company-channel notifications are off.
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Notification list */}
          {visible.length === 0 ? (
            <Box data-testid="notification-empty-state" sx={{ px: 2.5, py: 2.5, color: 'text.secondary' }}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                No notifications
              </Typography>
            </Box>
          ) : (
            <Box data-testid="notification-list" sx={notificationsListSx}>
              {visible.map(n => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  onDismiss={onDismiss}
                  onMarkAsRead={markNotificationRead}
                  onClose={onClose}
                  nowMs={nowMs}
                />
              ))}
            </Box>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default NotificationPanel;
